import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useClutch } from "./ClutchContext";
import { ReactComponent as StressLow } from "./stress-icons/StressLow.svg";
import { ReactComponent as StressNormal } from "./stress-icons/StressNormal.svg";
import { ReactComponent as StressElevated } from "./stress-icons/StressElevated.svg";
import { ReactComponent as StressHigh } from "./stress-icons/StressHigh.svg";
import { ReactComponent as StressVeryHigh } from "./stress-icons/StressVeryHigh.svg";

const SHOP_URL = "https://app.loopring.ai/Shop";

/* Stress Index (SI) → level, per CarePlix's 5-level scale */
const STRESS_LEVELS = [
  { point: 0,   label: "Low",       color: "#31B50C", Icon: StressLow },
  { point: 0.5, label: "Normal",    color: "#64BD40", Icon: StressNormal },
  { point: 1.5, label: "Elevated",  color: "#FAC02B", Icon: StressElevated },
  { point: 5,   label: "High",      color: "#DD3923", Icon: StressHigh },
  { point: 9,   label: "Very High", color: "#BF0715", Icon: StressVeryHigh },
];
const STRESS_DESC = "The body's reaction to a challenge or demand. There are 5 levels of stress.";
const resolveStress = (si) => {
  let chosen = STRESS_LEVELS[0];
  for (const lvl of STRESS_LEVELS) if (si >= lvl.point) chosen = lvl;
  return chosen;
};

const Result = () => {
  const navigate = useNavigate();
  const { config, result, details } = useClutch();
  const arcRef = useRef(null);
  const numRef = useRef(null);

  const pkg = config.healthPackage || {};
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ name: "", phone: "", email: "" });
  const [leadState, setLeadState] = useState("idle"); // idle | sending | done | error
  const [leadErr, setLeadErr] = useState("");

  const openLead = () => {
    setLead({ name: details.name || "", phone: details.phone || "", email: "" });
    setLeadState("idle");
    setLeadErr("");
    setShowLead(true);
  };

  const submitLead = async () => {
    const customerName = lead.name.trim();
    const customerPhone = lead.phone.trim();
    if (!customerName || !customerPhone) {
      setLeadErr("Please enter your name and phone number.");
      return;
    }
    setLeadState("sending");
    setLeadErr("");
    try {
      const res = await fetch("/api/biointel/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerName,
          customerPhone,
          customerEmail: lead.email.trim() || undefined,
          packageId: pkg.id,
          packageName: pkg.name,
        }),
      });
      if (!res.ok) throw new Error("Request failed");
      setLeadState("done");
    } catch (e) {
      setLeadState("error");
      setLeadErr("Couldn't submit right now. Please try again.");
    }
  };

  useEffect(() => {
    if (!result) { navigate("/"); return; }
    const C = 565;
    const arc = arcRef.current;
    if (arc) {
      arc.style.transition = "none";
      arc.style.strokeDashoffset = C;
      requestAnimationFrame(() => {
        arc.style.transition = "stroke-dashoffset 1.1s ease";
        arc.style.strokeDashoffset = C * (1 - result.score / 100);
      });
    }
    let n = 0;
    const iv = setInterval(() => {
      n += Math.ceil(result.score / 28);
      if (n >= result.score) { n = result.score; clearInterval(iv); }
      if (numRef.current) numRef.current.innerHTML = n + "<span>/100</span>";
    }, 38);
    return () => clearInterval(iv);
  }, [result, navigate]);

  if (!result) return null;

  const v = result.vitals || {};
  const hr = v.heart_rate ? Math.round(v.heart_rate) : result.peakBpm;
  const spo2 = v.oxy_sat_prcnt ? Math.round(v.oxy_sat_prcnt) : null;

  /* Real Stress Index from CarePlix vitals; fall back to the game stress so the
     card is always shown (e.g. in the no-camera demo/fallback path). */
  const rawSI = v.metadata?.heart_scores?.stress_index;
  const hasRealSI = rawSI != null && !isNaN(parseFloat(rawSI));
  const si = hasRealSI ? parseFloat(rawSI) : (Number(result.stressPct) || 0) / 10;
  const stress = resolveStress(si);
  const StressIcon = stress.Icon;
  const stressBarPct = Math.max(4, Math.min(100, (si / 10) * 100));

  return (
    <div className="cl-root">
      <div className="cl-bg" style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/stadium-bg.png)` }} />
      <div className="cl-wrap cl-scroll">
        <div className="cl-row">
          <div className="cl-logo"><b>Care</b><span>Plix</span><sup>✛</sup></div>
          <div className="cl-badge">FULL-TIME</div>
        </div>

        <div className="cl-scorering" style={{ marginTop: 8 }}>
          <svg viewBox="0 0 200 200">
            <circle cx="100" cy="100" r="90" fill="none" stroke="rgba(255,255,255,.1)" strokeWidth="9" />
            <circle ref={arcRef} cx="100" cy="100" r="90" fill="none" stroke="url(#clg)" strokeWidth="9" strokeLinecap="round" strokeDasharray="565" strokeDashoffset="565" />
            <defs><linearGradient id="clg" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor={config.accent} /><stop offset="1" stopColor={config.accent2} /></linearGradient></defs>
          </svg>
          <div className="cl-scorenum"><b ref={numRef}>0<span>/100</span></b><em>Composure</em></div>
        </div>

        <div className="cl-verdict" style={{ marginTop: 8 }}>{result.verdict}</div>
        <p className="cl-sub" style={{ textAlign: "center", marginTop: 8 }}>{result.blurb}</p>

        <div className="cl-statgrid" style={{ marginTop: 18 }}>
          <div className="cl-stat"><div className="v">{result.goals}<small>/{result.attempts}</small></div><div className="l">Goals scored</div></div>
          <div className="cl-stat"><div className="v">{hr}<small> bpm</small></div><div className="l">{result.vitals ? "Measured pulse" : "Peak pulse"}</div></div>
          <div className="cl-stat"><div className="v">{result.stressPct}<small>%</small></div><div className="l">Stress under pressure</div></div>
          <div className="cl-stat"><div className="v">{spo2 ? spo2 + "%" : (v.wellness_score ? Math.round(v.wellness_score) : "—")}</div><div className="l">{spo2 ? "Blood oxygen" : "Wellness score"}</div></div>
        </div>

        <div className="cl-stresscard" style={{ marginTop: 16, borderColor: stress.color + "66" }}>
          <div className="cl-stress-ic"><StressIcon width={52} height={52} /></div>
          <div className="cl-stress-body">
            <div className="cl-stress-top">
              <span className="cl-stress-label" style={{ color: stress.color }}>{stress.label}</span>
              <span className="cl-stress-si">SI {si.toFixed(1)}{!hasRealSI && <em>·est</em>}</span>
            </div>
            <div className="cl-stress-track">
              <div className="cl-stress-fill" style={{ width: stressBarPct + "%", background: stress.color }} />
            </div>
            <p className="cl-stress-desc">{STRESS_DESC}</p>
          </div>
        </div>

        <a className="cl-loopbanner" href={SHOP_URL} target="_blank" rel="noopener noreferrer" style={{ marginTop: 16 }}>
          <img src={`${process.env.PUBLIC_URL}/loop-banner.png`} alt="Introducing the Loop Ring Series X" />
        </a>

        <div className="cl-spacer" style={{ minHeight: 18 }} />
        <button className="cl-btn" onClick={openLead}>{config.cta.label}</button>
        <button className="cl-btn ghost" style={{ marginTop: 10 }} onClick={() => navigate("/")}>Play again</button>
        <p className="cl-foot" style={{ marginTop: 10 }}>{details.name ? details.name + " · " : ""}Composure measured by CarePlix rPPG</p>
      </div>

      {showLead && (
        <div className="cl-modal-bg" onClick={() => leadState !== "sending" && setShowLead(false)}>
          <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
            {leadState === "done" ? (
              <>
                <div className="cl-modal-icon">✓</div>
                <h3>You're all set!</h3>
                <p>Our health support team will connect with you shortly about your {pkg.name} ({pkg.price}).</p>
                <button className="cl-btn" style={{ marginTop: 18 }} onClick={() => setShowLead(false)}>Done</button>
              </>
            ) : (
              <>
                <h3>{pkg.name} · {pkg.price}</h3>
                <p>Want our health support team to connect with you to book your full body check-up?</p>
                <div className="cl-field" style={{ marginTop: 14 }}>
                  <label>Full name</label>
                  <input value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" autoComplete="off" />
                </div>
                <div className="cl-field" style={{ marginTop: 10 }}>
                  <label>Phone number</label>
                  <input value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value.replace(/[^\d+]/g, "") })} placeholder="10-digit mobile" inputMode="numeric" maxLength={14} />
                </div>
                <div className="cl-field" style={{ marginTop: 10 }}>
                  <label>Email <span style={{ color: "#7f93ab" }}>· optional</span></label>
                  <input value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="you@email.com" inputMode="email" autoComplete="off" />
                </div>
                {leadErr && <p className="cl-modal-err">{leadErr}</p>}
                <button className="cl-btn" style={{ marginTop: 16 }} disabled={leadState === "sending"} onClick={submitLead}>
                  {leadState === "sending" ? "Submitting…" : "Yes, connect me"}
                </button>
                <button className="cl-btn ghost" style={{ marginTop: 10 }} disabled={leadState === "sending"} onClick={() => setShowLead(false)}>Maybe later</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Result;
